import { BookingCalendarMonth } from '@components/Calendar/BookingCalendarMonth';
import { DayOverviewDrawer } from '@components/Calendar/DayOverviewDrawer';
import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { t } from '@lingui/core/macro';
import { BookingProposalStatus, BookingStatus, Currency } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import { brands } from 'noddi-types';
import { NoddiIcon } from 'noddi-ui-common';
import { MonthCalendarDayInfo, NoddiInput, NoddiSkeleton, useNoddiQueryParam } from 'noddi-ui-web';
import { formatCurrencyAmount, getEndOfMonth, getStartOfMonth, NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { useAuthContext } from 'noddi-web';
import { useMemo, useState } from 'react';
import { DateParam, withDefault } from 'use-query-params';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import { FleetCalendarBooking, getCalendarStatusKey, getDateKey } from '../../utils/fleetCalendarBookingDisplay';
import { FleetCalendarStats } from './FleetCalendarStats';

const VISIBLE_STATUSES: BookingStatus[] = [
  BookingStatus.CONFIRMED,
  BookingStatus.ASSIGNED_TO_ROUTE,
  BookingStatus.COMPLETED
];

export const FleetCalendarPage = () => {
  const [currentDate, setCurrentDate] = useNoddiQueryParam('from-date', withDefault(DateParam, new Date()));
  const [search, setSearch] = useState('');
  const [drawerDate, setDrawerDate] = useState<string | null>(null);

  const { currentUserGroupId } = useAuthContext();
  const { currentBrand } = useCurrentBrand();

  const fromDate = noddiFormatDate(getStartOfMonth(currentDate), NoddiDateFormats.API_DATE);
  const toDate = noddiFormatDate(getEndOfMonth(currentDate), NoddiDateFormats.API_DATE);

  const { data, isPending, isFetching, error } = noddiAsync.useGet({
    type: URLKeys.getPaginatedBookings,
    input: {
      userGroupIds: [currentUserGroupId as number],
      statusIds: VISIBLE_STATUSES,
      deliveryWindowStartsAtGte: fromDate,
      deliveryWindowStartsAtLte: toDate,
      pageSize: 200,
      pageIndex: 0
    },
    queryConfig: { placeholderData: keepPreviousData }
  });

  const { data: proposalData, error: proposalsError } = noddiAsync.useGet({
    type: URLKeys.getPaginatedBookingProposals,
    input: {
      userGroupIds: [currentUserGroupId as number],
      statuses: [BookingProposalStatus.PENDING],
      isVisibleCustomer: true
    }
  });

  const pendingProposals = (proposalData?.results ?? []).filter(
    (proposal) => currentBrand === brands.noddi || proposal.serviceDepartment.brand.domain === currentBrand
  );

  const bookingsByDate = useMemo(() => {
    const filtered: FleetCalendarBooking[] = [];
    const needle = search.trim().toLowerCase();

    for (const booking of data?.results ?? []) {
      if (getCalendarStatusKey(booking.status.value) === null) {
        continue;
      }

      if (needle.length > 0) {
        const matches = booking.bookingItems.some((item) => item.car.licensePlateNumber.toLowerCase().includes(needle));
        if (!matches) {
          continue;
        }
      }

      filtered.push(booking);
    }

    filtered.sort(
      (a, b) => new Date(a.deliveryWindowStartsAt).getTime() - new Date(b.deliveryWindowStartsAt).getTime()
    );

    const grouped = new Map<string, FleetCalendarBooking[]>();
    for (const booking of filtered) {
      const key = getDateKey(booking.deliveryWindowStartsAt);
      const list = grouped.get(key) ?? [];
      list.push(booking);
      grouped.set(key, list);
    }

    return grouped;
  }, [data, search]);

  const monthSummary = useMemo(() => {
    // eslint-disable-next-line no-restricted-syntax -- loop accumulator for the month summary
    let upcomingCount = 0;
    // eslint-disable-next-line no-restricted-syntax -- loop accumulator for the month summary
    let completedCount = 0;
    // eslint-disable-next-line no-restricted-syntax -- loop accumulator for the month summary
    let upcomingAmount = 0;
    // eslint-disable-next-line no-restricted-syntax -- loop accumulator for the month summary
    let completedAmount = 0;
    // eslint-disable-next-line no-restricted-syntax -- assigned via ??= from the first counted booking in the summary loop
    let currency: Currency | undefined;

    for (const dayBookings of bookingsByDate.values()) {
      for (const booking of dayBookings) {
        const key = getCalendarStatusKey(booking.status.value);
        if (key === 'upcoming') {
          upcomingCount += 1;
          upcomingAmount += booking.priceTotalGross.amount;
        } else if (key === 'completed') {
          completedCount += 1;
          completedAmount += booking.priceTotalGross.amount;
        } else {
          continue;
        }
        currency ??= booking.priceTotalGross.currency;
      }
    }

    const formatTotal = (amount: number) =>
      amount > 0 ? formatCurrencyAmount({ money: { amount, currency }, decimals: 0 }) : undefined;

    return {
      upcomingCount,
      completedCount,
      upcomingTotal: formatTotal(upcomingAmount),
      completedTotal: formatTotal(completedAmount)
    };
  }, [bookingsByDate]);

  const handleDayClick = (info: MonthCalendarDayInfo) => {
    setDrawerDate(info.dateString);
  };

  const drawerBookings = drawerDate ? (bookingsByDate.get(drawerDate) ?? []) : [];

  return (
    <ContentWrapperCustomerApp title={t`Booking calendar`} width='wide' hideBackButton error={error}>
      {isPending ? (
        <div className='flex flex-col gap-4'>
          {[1, 2, 3, 4].map((index) => (
            <NoddiSkeleton key={index} className='h-32' />
          ))}
        </div>
      ) : (
        <div className='flex flex-col gap-5'>
          <FleetCalendarStats
            upcomingCount={monthSummary.upcomingCount}
            completedCount={monthSummary.completedCount}
            upcomingTotal={monthSummary.upcomingTotal}
            completedTotal={monthSummary.completedTotal}
          />

          <BookingCalendarMonth
            currentDate={currentDate}
            onMonthChange={setCurrentDate}
            onDayClick={handleDayClick}
            bookingsByDate={bookingsByDate}
            loading={isFetching}
            headerActions={
              <div className='hidden md:block md:w-72 lg:w-96'>
                <NoddiInput
                  name='plate-search'
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t`Search for car by license plate…`}
                  startAdornment={<NoddiIcon name='Search' size='small' />}
                />
              </div>
            }
          />
        </div>
      )}

      <DayOverviewDrawer
        dateString={drawerDate}
        bookings={drawerBookings}
        pendingProposals={pendingProposals}
        proposalErrorNode={proposalsError ? <ApiErrorMessageWithTrans error={proposalsError} /> : undefined}
        onClose={() => setDrawerDate(null)}
      />
    </ContentWrapperCustomerApp>
  );
};
